// Transitions PR-5 — the guaranty truth-from-data panel (recon §10). States
// only what the roster actually shows: who the guarantors of record are,
// whether each guaranty is signed/filed (with the filed-document link), and
// that every guaranty remains in effect. §10.8's tiered-release conditions
// render as static contractual text — release is unmodeled and there is no
// release fee (D-25).

import { Link } from "react-router-dom";
import { Skeleton, StatusBadge, type StatusTone } from "@/design-system/components/ui";
import { useGuaranties } from "@/hooks/portfolioAdmin/useGuaranties";
import type { GuarantyStatus } from "@/api/portfolioAdmin/guarantiesApi";
import { GUARANTY_RELEASE_STATIC } from "./transitionsWizardCopy";

const STATUS_TONE: Record<GuarantyStatus, StatusTone> = {
  Pending: "neutral",
  Sent: "progress",
  Delivered: "info",
  Signed: "ok",
  Filed: "ok",
  Declined: "danger",
  Voided: "muted",
};

function statusLine(status: GuarantyStatus): string {
  switch (status) {
    case "Filed":
      return "signed & filed";
    case "Signed":
      return "signed";
    case "Delivered":
      return "delivered for signature";
    case "Sent":
      return "sent for signature";
    case "Declined":
      return "declined";
    case "Voided":
      return "voided";
    default:
      return "not yet sent";
  }
}

export function GuarantyFactsPanel({ loanId }: { loanId: string }) {
  const roster = useGuaranties(loanId);

  return (
    <div className="flex flex-col gap-space-3" data-testid="guaranty-facts-panel">
      <div>
        <p className="text-small font-semibold m-0 mb-space-1">Guarantors of record</p>
        {roster.isLoading ? (
          <Skeleton className="h-5 w-2/3" />
        ) : (roster.data?.rows.length ?? 0) === 0 ? (
          <p className="text-small text-gray-mid m-0" data-testid="guaranty-facts-empty">
            No guarantors are on this loan's roster.
          </p>
        ) : (
          <ul className="m-0 p-0 list-none flex flex-col gap-space-1" data-testid="guaranty-facts-rows">
            {roster.data!.rows.map((row) => (
              <li key={row.id} className="text-small flex items-center gap-space-2 flex-wrap">
                <StatusBadge tone={STATUS_TONE[row.status]}>{row.status}</StatusBadge>
                <span className="font-semibold">{row.guarantorName}</span>
                <span className="text-gray-mid">
                  — guaranty {statusLine(row.status)}
                  {row.filedDocumentId && (
                    <>
                      {" ("}
                      <Link
                        to={`/portfolio-admin/loans/${loanId}?tab=documents`}
                        className="underline"
                        data-testid={`guaranty-doc-link-${row.id}`}
                      >
                        view filed document
                      </Link>
                      {")"}
                    </>
                  )}
                  {"; it remains in effect."}
                </span>
              </li>
            ))}
          </ul>
        )}
      </div>

      <div className="rounded border border-border-light bg-white-warm p-space-3">
        <p className="text-small m-0" data-testid="guaranty-release-static">
          {GUARANTY_RELEASE_STATIC}
        </p>
      </div>
    </div>
  );
}
